import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { track, identify, EVENTS } from '@/lib/telemetry';

const AuthContext = createContext(null);

/**
 * Cookie-first auth store.
 *
 * Post-migration (Feb 2026):
 *   - `access_token` lives in an HttpOnly cookie (XSS-safe).
 *   - `csrf_token`   lives in a non-HttpOnly cookie; `lib/api.js` reads it and
 *                    attaches `X-CSRF-Token` on state-changing calls.
 *   - The old `cp_token` localStorage entry is still cleaned up on logout for
 *     any lingering sessions from before the migration.
 *
 * Public shape is unchanged: consumers still get `{ user, organization,
 * loading, login, register, logout, ... }`.
 */
const USER_KEY = 'cp_user';
const ORG_KEY = 'cp_org';
const LEGACY_TOKEN_KEY = 'cp_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [organization, setOrganizationState] = useState(() => {
    try {
      const raw = localStorage.getItem(ORG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const setOrganization = useCallback((org) => {
    setOrganizationState(org);
    if (org) {
      try { localStorage.setItem(ORG_KEY, JSON.stringify(org)); } catch { /* ignore */ }
    } else {
      localStorage.removeItem(ORG_KEY);
    }
  }, []);

  const loadOrganization = useCallback(async () => {
    try {
      const res = await api.get('/organizations/me');
      const org = res.data?.organization || null;
      setOrganization(org);
      return org;
    } catch {
      setOrganization(null);
      return null;
    }
  }, [setOrganization]);

  const ensureCsrf = useCallback(async () => {
    try { await api.get('/auth/csrf'); } catch { /* non-fatal */ }
  }, []);

  // Boot: rely on the httpOnly cookie (browser sends it automatically).
  // Attempts `/auth/me` first; if that succeeds we're authed.
  useEffect(() => {
    const validate = async () => {
      try {
        // Prime CSRF cookie first so subsequent mutating calls are pre-approved.
        await ensureCsrf();
        const res = await api.get('/auth/me');
        setUser(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        await loadOrganization();
      } catch {
        // Clean up any legacy artifacts.
        localStorage.removeItem(LEGACY_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(ORG_KEY);
        setUser(null);
        setOrganizationState(null);
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [ensureCsrf, loadOrganization]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      await loadOrganization();
      return res.data;
    } catch {
      return null;
    }
  }, [loadOrganization]);

  const login = useCallback(async (email, password) => {
    // POST /auth/login sets both access_token + csrf_token cookies.
    const res = await api.post('/auth/login', { email, password });
    const { user: u } = res.data;
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    // Legacy localStorage token no longer needed — clean it out.
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    setUser(u);
    identify(u);
    track(EVENTS.SIGN_IN, { method: 'password' });
    await loadOrganization();
    return u;
  }, [loadOrganization]);

  const register = useCallback(async (payload) => {
    const res = await api.post('/auth/register', payload);
    const { user: u } = res.data;
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    setUser(u);
    identify(u);
    track(EVENTS.SIGN_UP, { tier: u.subscription_tier });
    // New registrations have no org yet — leave null so RoleRoute triggers /setup.
    setOrganization(null);
    return u;
  }, [setOrganization]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* best effort */ }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
    setUser(null);
    setOrganizationState(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, organization, loading,
      login, register, logout,
      setOrganization, refreshUser, loadOrganization,
      isAuthed: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
